const TWO_WEEKS = 1000 * 60 * 60 * 24 * 7 * 2;
const SIX_MONTHS = 1000 * 60 * 60 * 24 * 30 * 6;
export const TIMEZONE_NY = 'America/New_York';

/**
 * @param date The date to be formatted
 * @param timeBetween Time between now and this date in ms (absolute value)
 */
function formatDateWithOffset(date, timeBetween) {
    if (timeBetween < TWO_WEEKS) {
        return date.getDayName() + ', ' + getMonthAndDay(date);
    } else if (timeBetween < SIX_MONTHS) {
        return getMonthAndDay(date);
    } else {
        return getMonthAndDay(date) + ', ' + date.getFullYear();
    }
}

function getMonthAndDay(date) {
    return date.getMonthName() + ' ' + date.getDate();
}

export function formatDateWithMonthName(date) {
    return getMonthAndDay(date) + ', ' + date.getFullYear();
}

/**
 * Formats the date in Year-Month-Day format (ISO 8601)
 * @param date The date to be formatted
 */
export const formatDate = function (date) {
    const year = date.getUTCFullYear();
    const month = _pad(date.getUTCMonth() + 1); // getMonth is zero-indexed
    const day = _pad(date.getUTCDate()); // getDate is one-indexed

    return year + '-' + month + '-' + day;
};

/**
 * Formats the date in Year-Month-Day format using local time
 */
export function formatDateLocal(myDate) {
    const year = myDate.getFullYear();
    const month = _pad(parseInt(myDate.getMonth()) + 1);
    const date = _pad(myDate.getDate());
    return year + '-' + month + '-' + date;
}

/**
 * Formats the date in a user-friendly abbreviated format (MM-dd).
 *
 * Relies implicitly on the global date.js.
 */
export const formatDateForDisplay = function formateDateForDisplay(date) {
    let month = date.getShortMonthName();
    if (month === 'Jun') {
        month = 'June';
    }

    return month + ' ' + date.getDate();
};

/**
 * See TimeUtil.java's formatDate method
 */
export const formatDateForVerboseDisplay = function formatDateForVerboseDisplay(date) {
// If in next two weeks, display Dweek Mon Day
// Else if in next six months, display Mon Day
// Else Mon Day, Year
    const now = new Date();

    let timeBetween;
    if (now > date) {
        timeBetween = now - date;
    } else {
        timeBetween = date - now;
    }

    return formatDateWithOffset(date, timeBetween);
};

/**
 * Parses the yyyy-MM-dd String into a Date. (We cannot safely use new Date(dateString)
 * for this because Date.parse is broken on old-ish versions of Safari - see
 * WS-21365 - even though the ES5 standard requires yyyy-MM-dd support.)
 *
 * Time-of-day defaults to noon to minimize cases where date differs between
 * user's browser and our server.
 * @param dateString The yyyy-MM-dd String to be parsed
 */
export const parseDate = function (dateString) {
    // Sometimes JSONAdapter serializes dates as epoch longs unexpectedly.
    // Handle that.
    if (typeof dateString === 'number') {
        return new Date(dateString);
    }

    const parts = dateString.split('-');

    // Months are zero-indexed, years/days aren't.
    return new Date(parts[0], parts[1] - 1, parts[2], 12);
};

/**
 * Parse a ISO datestamp into a Date object in en-US locale (mm/dd/yyyy)
 * @param date
 */
export const parseTimestampToEnUs = function (date) {
    return new Date(date).toLocaleDateString('en-us');
};

/**
 * Parse the yyyy-MM-dd String into a Date if the String is truthy. Otherwise, return null.
 * @param dateString The yyyy-MM-dd String to be parsed
 */
export const parseNullableDate = function (dateString) {
    if (dateString) {
        return parseDate(dateString);
    } else {
        return null;
    }
};

/**
 * True if aDate and anotherDate have equal values or are both falsy.
 * @param {Date} aDate
 * @param {Date} anotherDate
 * @returns {boolean|*}
 */
export const equals = function (aDate, anotherDate) {
    return (!aDate && !anotherDate) || (aDate && anotherDate && aDate.getTime() === anotherDate.getTime());
};

/**
 * Returns a new date with time-of-day truncated to midnight _in the browser's local time_. For use with APIs
 * that default to local time zones; otherwise, UTC-friendly methods are preferable (since they'll work better
 * with APIs like JSON.serialize).
 * @param date
 * @returns {Date} A new Date with time-of-day set to midnight (in THE BROWSER'S LOCAL TIME) on the same date.
 */
export const truncateLocal = function (date) {
    const truncatedDate = new Date(date);

    // This is intentionally not UTC, because jQuery datepicker returns local-time-zone dates
    truncatedDate.setHours(0, 0, 0, 0);
    return truncatedDate;
};

export const _pad = function (num) {
    if (num < 10) {
        return '0' + num;
    } else {
        return '' + num;
    }
};

/**
 * Return the number of days between fromDate and toDate. Negative if toDate is
 * before fromDate. Rounded due to DST etc.
 * @param {Date} fromDate
 * @param {Date} toDate
 */
export const daysBetween = function (fromDate, toDate) {
    const truncatedFromDate = new Date(fromDate);
    truncatedFromDate.setHours(0, 0, 0, 0);

    const truncatedToDate = new Date(toDate);
    truncatedToDate.setHours(0, 0, 0, 0);

    return Math.round(fractionalDaysBetween(truncatedFromDate, truncatedToDate));
};

const fractionalDaysBetween = function (fromDate, toDate) {
    // Based on http://stackoverflow.com/a/2627493/4318200
    const oneDay = 24 * 60 * 60 * 1000;

    return (toDate.getTime() - fromDate.getTime()) / oneDay;
};

const daysBetweenRoundedUp = function (fromDate, toDate) {
    return Math.ceil(fractionalDaysBetween(fromDate, toDate));
};

const noonToday = function () {
    const date = new Date();
    date.setHours(12, 0, 0, 0);
    return date;
};

export const daysUntilRoundedUp = function (date) {
    // Why noon? Because that's the default time-of-day produced by this.parseDate.
    const today = noonToday();
    return daysBetweenRoundedUp(today, date);
};

export function formateDateYYYYMMDD(dateObj) {
    const date = _pad(dateObj.getDate());
    const month = _pad(dateObj.getMonth() + 1); // months are zero based
    const year = dateObj.getFullYear();
    return '' + year + month + date;
}
